import React, { useId, useRef } from 'react';
import PropTypes from 'prop-types';
import './Slider.css';

/**
 * RangeSlider — Design System
 *
 * Two-thumb range control (a price range): label left, formatted "a – b"
 * right, a track filled between the thumbs. Built from two overlaid native
 * <input type="range"> elements, so each thumb keeps the browser's keyboard
 * stepping (arrows / Home / End / Page keys) and screen-reader semantics —
 * the same foundation as Slider, whose thumb styles it reuses (.ds-slider__input).
 *
 * The thumbs never cross: each is held `minGap` away from the other.
 * `onChange` reports every move (live display); `onChangeEnd` fires once a
 * thumb is released (pointer up / key up) — commit expensive work, such as
 * re-filtering a product grid, there.
 *
 * No Figma node yet (like Slider) — not in COMPONENTS_CONFIG.
 */
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const RangeSlider = ({
  label = '',
  value,
  min = 0,
  max = 100,
  step = 1,
  minGap = 0,
  onChange,
  onChangeEnd,
  formatValue,
  showValue = true,
  minLabel = 'Minimum',
  maxLabel = 'Maximum',
  disabled = false,
  className = '',
  ...rest
}) => {
  const labelId = useId();
  const span = max - min;

  const lo = clamp(Math.min(value[0], value[1]), min, max);
  const hi = clamp(Math.max(value[0], value[1]), min, max);

  const format = (v) => (typeof formatValue === 'function' ? formatValue(v) : String(v));
  const pct = (v) => (span > 0 ? ((v - min) / span) * 100 : 0);

  // The pair as last reported — what onChangeEnd commits. `moved` keeps a
  // release without a move (a click on a thumb, a Tab key-up) from committing.
  const latest = useRef([lo, hi]);
  const moved = useRef(false);
  if (!moved.current) latest.current = [lo, hi];

  const report = (next) => {
    latest.current = next;
    moved.current = true;
    if (onChange) onChange(next);
  };

  const commit = () => {
    if (!moved.current) return;
    moved.current = false;
    if (onChangeEnd) onChangeEnd(latest.current);
  };

  const handleMin = (e) => {
    const [, b] = latest.current;
    report([clamp(Number(e.target.value), min, b - minGap), b]);
  };

  const handleMax = (e) => {
    const [a] = latest.current;
    report([a, clamp(Number(e.target.value), a + minGap, max)]);
  };

  // Both thumbs on one spot: the upper half hands the top layer to the min
  // thumb, so a pair pushed against the max end can still be dragged back.
  const minOnTop = lo > min + span / 2;

  const rootClass = [
    'ds-slider',
    'ds-range-slider',
    disabled && 'ds-slider--disabled',
    className,
  ].filter(Boolean).join(' ');

  const thumbProps = {
    type: 'range',
    min,
    max,
    step,
    disabled,
    onPointerUp: commit,
    onTouchEnd: commit,
    onKeyUp: commit,
    onBlur: commit,
  };

  return (
    <div
      className={rootClass}
      role="group"
      aria-labelledby={label ? labelId : undefined}
      style={{ '--ds-range-from': `${pct(lo)}%`, '--ds-range-to': `${pct(hi)}%` }}
      {...rest}
    >
      {(label || showValue) && (
        <div className="ds-slider__header">
          {label && <span id={labelId} className="ds-slider__label typo-h5">{label}</span>}
          {showValue && (
            <span className="ds-slider__value typo-body-sm">{`${format(lo)} – ${format(hi)}`}</span>
          )}
        </div>
      )}

      <div className="ds-range-slider__control">
        <span className="ds-range-slider__track" aria-hidden="true" />
        <input
          {...thumbProps}
          className={`ds-slider__input ds-range-slider__input${minOnTop ? ' ds-range-slider__input--top' : ''}`}
          value={lo}
          aria-label={minLabel}
          aria-valuetext={format(lo)}
          onChange={handleMin}
        />
        <input
          {...thumbProps}
          className="ds-slider__input ds-range-slider__input"
          value={hi}
          aria-label={maxLabel}
          aria-valuetext={format(hi)}
          onChange={handleMax}
        />
      </div>
    </div>
  );
};

RangeSlider.propTypes = {
  /** Text shown above the track, left-aligned; names the group for assistive tech */
  label: PropTypes.string,
  /** Controlled pair [low, high]; out-of-bounds values are clamped for display */
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  /** Smallest distance the thumbs keep between them */
  minGap: PropTypes.number,
  /** Every move — receives the new [low, high] */
  onChange: PropTypes.func,
  /** Once a thumb is released (pointer up / key up) — receives the final [low, high] */
  onChangeEnd: PropTypes.func,
  /** Formats each value in the header and aria-valuetext, e.g. (v) => `${v} kr` */
  formatValue: PropTypes.func,
  /** Show "low – high" on the right of the header */
  showValue: PropTypes.bool,
  /** Accessible name of the low thumb */
  minLabel: PropTypes.string,
  /** Accessible name of the high thumb */
  maxLabel: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default RangeSlider;
